import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Upload, 
  Search, 
  FileText, 
  Sparkles, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle2,
  FileSearch,
  Bell,
  Clock,
  X,
  Calendar,
  FolderOpen,
  Share2,
  Link as LinkIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { TYPE_CONFIG, CATEGORY_CONFIG, getTypeConfig, getCategoryConfig, getTypeLabel, getCategoryLabel } from "@/constants/ui-mapping";

interface DemoStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  mockup: React.ReactNode;
}

export function InteractiveDemo() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  // Autoplay effect
  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(() => {
        setCurrentStep((s) => (s + 1) % 5);
      }, 8000);
    }
    
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying]);

  const pauseAutoPlay = () => {
    setIsAutoPlaying(false);
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  const steps: DemoStep[] = [
    {
      id: "upload",
      title: "Importez vos documents",
      description: "Glissez-déposez vos fichiers pour les importer instantanément.",
      icon: <Upload className="w-5 h-5" />,
      color: "from-primary to-primary/80",
      mockup: <UploadMockup />
    },
    {
      id: "ai-search",
      title: "Recherche intelligente",
      description: "Tapez votre question en langage naturel pour trouver vos documents.",
      icon: <Search className="w-5 h-5" />,
      color: "from-violet-500 to-purple-600",
      mockup: <AISearchMockup />
    },
    {
      id: "classify",
      title: "Classification automatique",
      description: "L'IA identifie et catégorise automatiquement vos documents.",
      icon: <Sparkles className="w-5 h-5" />,
      color: "from-emerald-500 to-emerald-600",
      mockup: <ClassifyMockup />
    },
    {
      id: "collector",
      title: "Collecteur de dossiers",
      description: "Créez un dossier complet et partagez-le en un clic.",
      icon: <FolderOpen className="w-5 h-5" />,
      color: "from-amber-500 to-orange-500",
      mockup: <CollectorMockup />
    },
    {
      id: "alerts",
      title: "Alertes d'expiration",
      description: "Ne ratez plus aucune échéance importante.",
      icon: <Bell className="w-5 h-5" />,
      color: "from-rose-500 to-red-500",
      mockup: <AlertsMockup />
    }
  ];

  const nextStep = () => {
    setCurrentStep((s) => (s + 1) % steps.length);
    pauseAutoPlay();
  };

  const prevStep = () => {
    setCurrentStep((s) => (s - 1 + steps.length) % steps.length);
    pauseAutoPlay();
  };

  const goToStep = (index: number) => {
    setCurrentStep(index);
    pauseAutoPlay();
  };

  return (
    <div className="w-full h-full flex flex-col bg-gradient-to-br from-slate-50 via-white to-primary/5">
      {/* Header with step indicators */}
      <div className="flex items-center justify-between p-4 border-b border-border/50 bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-xl bg-gradient-to-br ${steps[currentStep].color} shadow-lg`}>
            <span className="text-white">{steps[currentStep].icon}</span>
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm md:text-base">
              {steps[currentStep].title}
            </h3>
            <p className="text-muted-foreground text-xs hidden md:block">
              {steps[currentStep].description}
            </p>
          </div>
        </div>
        
        {/* Step dots with progress indicator */}
        <div className="flex items-center gap-1.5">
          {steps.map((step, index) => (
            <button
              key={step.id}
              onClick={() => goToStep(index)}
              className="relative w-2.5 h-2.5 rounded-full transition-all overflow-hidden"
            >
              <div className={`absolute inset-0 rounded-full ${
                index === currentStep 
                  ? 'bg-primary' 
                  : index < currentStep 
                    ? 'bg-primary/60' 
                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`} />
              {/* Autoplay progress indicator */}
              {isAutoPlaying && index === currentStep && (
                <motion.div 
                  className="absolute inset-0 rounded-full bg-primary/30"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 8, ease: "linear" }}
                  style={{ transformOrigin: 'left' }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Demo Content */}
      <div className="flex-1 relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0"
          >
            {steps[currentStep].mockup}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-md z-10"
          onClick={prevStep}
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-md z-10"
          onClick={nextStep}
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Footer */}
      <div className="p-3 bg-white/80 backdrop-blur-sm border-t border-border/50 text-center">
        <p className="text-xs text-muted-foreground">
          Étape {currentStep + 1} sur {steps.length} • <span className="text-primary font-medium">Essayez l'interaction !</span>
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// UPLOAD MOCKUP - Sources & automatic import (Professional version)
// ═══════════════════════════════════════════════════════════════

// Brand icons as inline SVGs for professional look
const GmailIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M22 6.5V17.5C22 18.88 20.88 20 19.5 20H4.5C3.12 20 2 18.88 2 17.5V6.5C2 5.12 3.12 4 4.5 4H19.5C20.88 4 22 5.12 22 6.5Z" fill="#F1F1F1"/>
    <path d="M22 6.5L12 13L2 6.5" stroke="#EA4335" strokeWidth="2" strokeLinecap="round"/>
    <path d="M2 6.5V17.5C2 18.88 3.12 20 4.5 20" stroke="#4285F4" strokeWidth="2" strokeLinecap="round"/>
    <path d="M22 6.5V17.5C22 18.88 20.88 20 19.5 20" stroke="#34A853" strokeWidth="2" strokeLinecap="round"/>
    <path d="M4.5 20H19.5" stroke="#FBBC05" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const DropboxIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="#0061FF">
    <path d="M6 2L0 6L6 10L0 14L6 18L12 14L18 18L24 14L18 10L24 6L18 2L12 6L6 2ZM6 4.5L10.5 7.5L6 10.5L1.5 7.5L6 4.5ZM18 4.5L22.5 7.5L18 10.5L13.5 7.5L18 4.5ZM12 8.5L16.5 11.5L12 14.5L7.5 11.5L12 8.5ZM6 12.5L10.5 15.5L6 18.5L1.5 15.5L6 12.5ZM18 12.5L22.5 15.5L18 18.5L13.5 15.5L18 12.5Z"/>
  </svg>
);

const GoogleDriveIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M8 2L1 14H8L15 2H8Z" fill="#4285F4"/>
    <path d="M15 2L8 14L12 21L19 9L15 2Z" fill="#FBBC05"/>
    <path d="M1 14L5 21H19L15 14H1Z" fill="#34A853"/>
  </svg>
);

const ScannerIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7V5a2 2 0 0 1 2-2h2"/>
    <path d="M17 3h2a2 2 0 0 1 2 2v2"/>
    <path d="M21 17v2a2 2 0 0 1-2 2h-2"/>
    <path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
    <rect x="7" y="7" width="10" height="10" rx="1"/>
  </svg>
);

function UploadMockup() {
  const [connectedSources, setConnectedSources] = useState<string[]>([]);
  const [files, setFiles] = useState<{ name: string; source: string; status: 'importing' | 'done' }[]>([]);
  const [isConnecting, setIsConnecting] = useState<string | null>(null);

  const sources = [
    { 
      id: 'gmail', 
      name: 'Gmail', 
      icon: GmailIcon,
      gradient: 'from-[#EA4335] to-[#FBBC05]',
      bgConnected: 'bg-red-50 border-red-200',
      iconBg: 'bg-white',
      files: [
        { name: 'Facture EDF - Sept. 2024', type: 'pdf' },
        { name: 'Relevé Banque Populaire', type: 'pdf' },
        { name: 'Attestation MMA', type: 'pdf' }
      ]
    },
    { 
      id: 'dropbox', 
      name: 'Dropbox', 
      icon: DropboxIcon,
      gradient: 'from-[#0061FF] to-[#0061FF]',
      bgConnected: 'bg-blue-50 border-blue-200',
      iconBg: 'bg-white',
      files: [
        { name: 'Contrat Location 2024', type: 'pdf' },
        { name: 'Devis Travaux', type: 'pdf' }
      ]
    },
    { 
      id: 'gdrive', 
      name: 'Google Drive', 
      icon: GoogleDriveIcon,
      gradient: 'from-[#4285F4] via-[#34A853] to-[#FBBC05]',
      bgConnected: 'bg-green-50 border-green-200',
      iconBg: 'bg-white',
      files: [
        { name: 'Bulletins de salaire 2024', type: 'pdf' },
        { name: 'Avis imposition 2023', type: 'pdf' }
      ]
    },
    { 
      id: 'scanner', 
      name: 'Scanner', 
      icon: ScannerIcon,
      gradient: 'from-emerald-500 to-teal-500',
      bgConnected: 'bg-emerald-50 border-emerald-200',
      iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-500',
      iconColor: 'text-white',
      files: [
        { name: 'Ordonnance Dr. Martin', type: 'jpg' },
        { name: 'Carte vitale', type: 'jpg' }
      ]
    }
  ];

  const connectSource = (sourceId: string) => {
    if (isConnecting || connectedSources.includes(sourceId)) return;
    
    const source = sources.find(s => s.id === sourceId);
    if (!source) return;

    setIsConnecting(sourceId);
    
    setTimeout(() => {
      setConnectedSources(prev => [...prev, sourceId]);
      setIsConnecting(null);
      
      // Import files from this source
      source.files.forEach((file, i) => {
        setTimeout(() => {
          setFiles(f => [...f, { name: file.name, source: sourceId, status: 'importing' }]);
          setTimeout(() => {
            setFiles(f => f.map(existingFile => 
              existingFile.name === file.name ? { ...existingFile, status: 'done' } : existingFile
            ));
          }, 600);
        }, i * 400);
      });
    }, 800);
  };

  const getSourceData = (sourceId: string) => sources.find(s => s.id === sourceId);

  const reset = () => {
    setFiles([]);
    setConnectedSources([]);
    setIsConnecting(null);
  };

  return (
    <div className="h-full p-4 md:p-6 flex flex-col bg-gradient-to-br from-slate-50 to-white">
      {/* Header */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-foreground mb-1">Connectez vos sources</h4>
        <p className="text-xs text-muted-foreground">Cliquez pour synchroniser automatiquement</p>
      </div>

      {/* Sources Grid - Professional cards */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {sources.map((source) => {
          const isConnected = connectedSources.includes(source.id);
          const isLoading = isConnecting === source.id;
          const Icon = source.icon;
          
          return (
            <motion.button
              key={source.id}
              onClick={() => connectSource(source.id)}
              disabled={isLoading || isConnected}
              className={cn(
                "relative p-4 rounded-2xl border-2 transition-all duration-300 text-left",
                isConnected 
                  ? `${source.bgConnected}` 
                  : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md',
                isLoading && 'opacity-80'
              )}
              whileHover={!isConnected && !isLoading ? { scale: 1.02, y: -2 } : {}}
              whileTap={!isConnected && !isLoading ? { scale: 0.98 } : {}}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center shadow-sm",
                  source.iconBg,
                  source.iconColor
                )}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground">{source.name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {isConnected ? `${source.files.length} docs importés` : 'Cliquez pour connecter'}
                  </p>
                </div>
              </div>
              
              {/* Status indicator */}
              {isLoading && (
                <motion.div 
                  className="absolute top-3 right-3 w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                />
              )}
              {isConnected && !isLoading && (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 25 }}
                  className="absolute top-3 right-3 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg"
                >
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Imported Files List */}
      <div className="flex-1 overflow-y-auto">
        {files.length === 0 && !isConnecting && (
          <div className="flex flex-col items-center justify-center h-full text-center py-4">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
              <Upload className="w-7 h-7 text-slate-400" />
            </div>
            <p className="text-sm text-muted-foreground font-medium">
              Vos documents apparaîtront ici
            </p>
          </div>
        )}
        
        <AnimatePresence>
          {files.length > 0 && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-2"
            >
              {files.map((file, index) => {
                const sourceData = getSourceData(file.source);
                const Icon = sourceData?.icon || Upload;
                
                return (
                  <motion.div
                    key={`${file.name}-${index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-200 shadow-sm"
                  >
                    <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-[10px] text-muted-foreground">via {sourceData?.name}</p>
                    </div>
                    {file.status === 'importing' ? (
                      <motion.div 
                        className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                      />
                    ) : (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500 }}
                      >
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Summary Footer */}
      {files.length > 0 && files.every(f => f.status === 'done') && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-800">{files.length} documents importés</p>
                <p className="text-[10px] text-emerald-600">{connectedSources.length} source(s) connectée(s)</p>
              </div>
            </div>
            <button
              onClick={reset}
              className="text-xs text-emerald-700 hover:text-emerald-800 font-medium hover:underline"
            >
              Réinitialiser
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// AI SEARCH MOCKUP - Interactive search with typing
// ═══════════════════════════════════════════════════════════════
function AISearchMockup() {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [explanation, setExplanation] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const exampleQueries = [
    "Mes factures de plus de 500€",
    "Contrats signés en 2024",
    "Documents d'assurance auto"
  ];

  const mockResults: Record<string, { results: any[], explanation: string }> = {
    "facture": {
      results: [
        { name: "Facture EDF - 623,45€", match: 95, type: "invoice", category: "housing", date: "15 mars 2024" },
        { name: "Facture Orange Pro - 890,00€", match: 92, type: "invoice", category: "tech", date: "2 avril 2024" },
        { name: "Facture Assurance - 1 245,00€", match: 88, type: "invoice", category: "insurance", date: "20 jan 2024" },
      ],
      explanation: "J'ai trouvé 3 factures dont le montant dépasse 500€."
    },
    "contrat": {
      results: [
        { name: "Contrat de location - Appart Paris", match: 97, type: "contract", category: "housing", date: "1 jan 2024" },
        { name: "Contrat de travail - CDI", match: 94, type: "contract", category: "work", date: "15 mars 2024" },
      ],
      explanation: "Voici les contrats signés en 2024."
    },
    "assurance": {
      results: [
        { name: "Attestation assurance auto AXA", match: 98, type: "insurance", category: "vehicle", date: "1 jan 2024" },
        { name: "Carte verte véhicule", match: 91, type: "insurance", category: "vehicle", date: "1 jan 2024" },
      ],
      explanation: "Documents d'assurance automobile trouvés."
    }
  };

  const handleSearch = (searchQuery?: string) => {
    const q = searchQuery || query;
    if (!q.trim()) return;
    
    setIsSearching(true);
    setResults([]);
    setExplanation("");
    
    setTimeout(() => {
      const key = Object.keys(mockResults).find(k => q.toLowerCase().includes(k)) || "facture";
      setResults(mockResults[key].results);
      setExplanation(mockResults[key].explanation);
      setIsSearching(false);
    }, 1200);
  };

  const handleExampleClick = (example: string) => {
    setQuery(example);
    handleSearch(example);
  };

  return (
    <div className="h-full p-6 flex flex-col">
      {/* Search Bar */}
      <div className="relative mb-4">
        <div className="flex items-center gap-2 bg-white rounded-xl p-2 shadow-sm border border-border/50">
          <Sparkles className="w-5 h-5 text-primary ml-2" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Posez votre question..."
            className="border-0 focus-visible:ring-0 bg-transparent"
          />
          <Button 
            size="sm" 
            onClick={() => handleSearch()}
            disabled={isSearching || !query.trim()}
            className="shrink-0"
          >
            {isSearching ? (
              <motion.div 
                className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            ) : (
              <Search className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Example Queries */}
      {results.length === 0 && !isSearching && (
        <div className="mb-4">
          <p className="text-xs text-muted-foreground mb-2">Essayez :</p>
          <div className="flex flex-wrap gap-2">
            {exampleQueries.map((example) => (
              <button
                key={example}
                onClick={() => handleExampleClick(example)}
                className="text-xs px-3 py-1.5 bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* AI Explanation */}
      <AnimatePresence>
        {explanation && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg mb-4"
          >
            <Sparkles className="w-4 h-4 text-primary mt-0.5" />
            <p className="text-sm text-foreground">{explanation}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results */}
      <div className="flex-1 overflow-y-auto space-y-2">
        <AnimatePresence>
          {isSearching && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center py-8"
            >
              <motion.div 
                className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              <span className="ml-3 text-muted-foreground">Recherche en cours...</span>
            </motion.div>
          )}
          {results.map((result, i) => (
            <motion.div
              key={result.name}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm border border-border/50 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-xl">
                {getTypeConfig(result.type).emoji}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{result.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={cn(
                    "text-[10px] px-1.5 py-0.5 rounded-full font-medium",
                    getCategoryConfig(result.category).color
                  )}>
                    {getCategoryLabel(result.category)}
                  </span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto">
                    {getTypeLabel(result.type)}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">{result.date}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className={`text-sm font-bold ${result.match >= 90 ? 'text-emerald-500' : 'text-amber-500'}`}>
                  {result.match}%
                </span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CLASSIFY MOCKUP - Professional AI classification
// ═══════════════════════════════════════════════════════════════

// Document type icons
const InvoiceIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <path d="M14 2v6h6"/>
    <path d="M8 13h8"/>
    <path d="M8 17h8"/>
    <path d="M8 9h2"/>
  </svg>
);

const IdCardIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2"/>
    <circle cx="8" cy="12" r="2"/>
    <path d="M14 10h4"/>
    <path d="M14 14h2"/>
  </svg>
);

const ContractIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <path d="M14 2v6h6"/>
    <path d="M12 18v-6"/>
    <path d="M9 15l3 3 3-3"/>
  </svg>
);

const InsuranceIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);

function ClassifyMockup() {
  const [classified, setClassified] = useState<number[]>([]);
  const [started, setStarted] = useState(false);

  const documents = [
    { 
      name: "scan_facture_001.pdf", 
      originalName: "Fichier non identifié",
      type: "invoice", 
      category: "housing",
      result: { type: "Facture", category: "Logement", confidence: 98 },
      icon: InvoiceIcon,
      gradient: "from-blue-500 to-blue-600"
    },
    { 
      name: "IMG_2024_03_15.jpg", 
      originalName: "Photo importée",
      type: "identity", 
      category: "administrative",
      result: { type: "Pièce d'identité", category: "Administratif", confidence: 95 },
      icon: IdCardIcon,
      gradient: "from-violet-500 to-purple-600"
    },
    { 
      name: "document_final.pdf", 
      originalName: "Document PDF",
      type: "contract", 
      category: "work",
      result: { type: "Contrat", category: "Travail", confidence: 92 },
      icon: ContractIcon,
      gradient: "from-emerald-500 to-teal-600"
    },
    { 
      name: "mail_attachment.pdf", 
      originalName: "Pièce jointe email",
      type: "insurance", 
      category: "vehicle",
      result: { type: "Assurance", category: "Véhicule", confidence: 97 },
      icon: InsuranceIcon,
      gradient: "from-amber-500 to-orange-500"
    },
  ];

  const startClassification = () => {
    if (started) return;
    setStarted(true);
    setClassified([]);
    documents.forEach((_, i) => {
      setTimeout(() => {
        setClassified(c => [...c, i]);
      }, 800 * (i + 1));
    });
  };

  useEffect(() => {
    const timer = setTimeout(startClassification, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="h-full p-4 md:p-6 flex flex-col bg-gradient-to-br from-slate-50 to-white">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-foreground">Classification IA</h4>
          <p className="text-xs text-muted-foreground">Analyse automatique en cours...</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs text-muted-foreground">{classified.length}/{documents.length}</span>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="flex-1 grid grid-cols-2 gap-3">
        {documents.map((doc, i) => {
          const isClassified = classified.includes(i);
          const Icon = doc.icon;
          
          return (
            <motion.div
              key={doc.name}
              className={cn(
                "relative bg-white rounded-2xl p-4 border-2 transition-all overflow-hidden",
                isClassified 
                  ? 'border-slate-200 shadow-lg' 
                  : 'border-dashed border-slate-300'
              )}
              onClick={() => !started && startClassification()}
              whileHover={{ scale: 1.02 }}
            >
              {/* Background gradient when classified */}
              {isClassified && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.05 }}
                  className={`absolute inset-0 bg-gradient-to-br ${doc.gradient}`}
                />
              )}
              
              <div className="relative z-10">
                {/* Icon and file info */}
                <div className="flex items-start gap-3 mb-3">
                  <div className={cn(
                    "w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500",
                    isClassified 
                      ? `bg-gradient-to-br ${doc.gradient} shadow-lg` 
                      : 'bg-slate-100'
                  )}>
                    <Icon className={cn(
                      "w-5 h-5 transition-colors duration-500",
                      isClassified ? 'text-white' : 'text-slate-400'
                    )} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-foreground truncate">
                      {isClassified ? doc.name : doc.originalName}
                    </p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {isClassified ? 'Identifié' : 'En analyse...'}
                    </p>
                  </div>
                </div>
                
                {/* Classification result */}
                <AnimatePresence mode="wait">
                  {isClassified ? (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className="space-y-2"
                    >
                      <div className="flex flex-wrap gap-1">
                        <span className={cn(
                          "inline-flex items-center px-2 py-1 rounded-lg text-[10px] font-semibold text-white",
                          `bg-gradient-to-r ${doc.gradient}`
                        )}>
                          {doc.result.type}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-lg text-[10px] font-medium bg-slate-100 text-slate-600">
                          {doc.result.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${doc.result.confidence}%` }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className={`h-full bg-gradient-to-r ${doc.gradient}`}
                          />
                        </div>
                        <span className="text-[10px] font-bold text-emerald-600">{doc.result.confidence}%</span>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div 
                      className="space-y-2"
                      animate={{ opacity: [0.3, 0.6, 0.3] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <div className="flex gap-1">
                        <div className="h-5 w-16 bg-slate-100 rounded-lg" />
                        <div className="h-5 w-12 bg-slate-100 rounded-lg" />
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>
      
      {/* Footer */}
      {classified.length === documents.length && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-800">{documents.length} documents classifiés</p>
                <p className="text-[10px] text-emerald-600">Précision moyenne : 96%</p>
              </div>
            </div>
            <button
              onClick={() => { setStarted(false); setClassified([]); setTimeout(startClassification, 200); }}
              className="text-xs text-emerald-700 hover:text-emerald-800 font-medium hover:underline"
            >
              Relancer
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COLLECTOR MOCKUP - Professional folder creation
// ═══════════════════════════════════════════════════════════════

const TaxIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="18" rx="2"/>
    <path d="M8 7v10"/>
    <path d="M12 7v10"/>
    <path d="M16 7v10"/>
  </svg>
);

const PayslipIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="M6 8h.01"/>
    <path d="M10 8h8"/>
    <path d="M6 12h.01"/>
    <path d="M10 12h8"/>
    <path d="M6 16h.01"/>
    <path d="M10 16h8"/>
  </svg>
);

const BankIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18"/>
    <path d="M3 10h18"/>
    <path d="M5 6l7-3 7 3"/>
    <path d="M4 10v11"/>
    <path d="M20 10v11"/>
    <path d="M8 14v3"/>
    <path d="M12 14v3"/>
    <path d="M16 14v3"/>
  </svg>
);

const CertificateIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <path d="M14 2v6h6"/>
    <circle cx="12" cy="14" r="2"/>
    <path d="M12 16v2"/>
  </svg>
);

function CollectorMockup() {
  const [step, setStep] = useState<'search' | 'collecting' | 'ready' | 'shared'>('search');
  const [collectedDocs, setCollectedDocs] = useState<number[]>([]);

  const documents = [
    { name: "Avis d'imposition 2023", icon: TaxIcon, gradient: "from-blue-500 to-indigo-600" },
    { name: "Bulletin de salaire - Mars", icon: PayslipIcon, gradient: "from-violet-500 to-purple-600" },
    { name: "Bulletin de salaire - Avril", icon: PayslipIcon, gradient: "from-violet-500 to-purple-600" },
    { name: "Bulletin de salaire - Mai", icon: PayslipIcon, gradient: "from-violet-500 to-purple-600" },
    { name: "Attestation employeur", icon: CertificateIcon, gradient: "from-emerald-500 to-teal-600" },
    { name: "Relevé de compte BNP", icon: BankIcon, gradient: "from-amber-500 to-orange-500" },
  ];

  const startCollection = () => {
    if (step !== 'search') return;
    setStep('collecting');
    setCollectedDocs([]);
    
    documents.forEach((_, i) => {
      setTimeout(() => {
        setCollectedDocs(c => [...c, i]);
        if (i === documents.length - 1) {
          setTimeout(() => setStep('ready'), 500);
        }
      }, 350 * (i + 1));
    });
  };

  const shareFolder = () => setStep('shared');
  const reset = () => { setStep('search'); setCollectedDocs([]); };

  return (
    <div className="h-full p-4 md:p-6 flex flex-col bg-gradient-to-br from-amber-50/50 to-white">
      {/* Search bar */}
      <div className="mb-4">
        <div 
          className={cn(
            "flex items-center gap-3 bg-white rounded-2xl p-3 border-2 cursor-pointer transition-all",
            step === 'search' 
              ? 'border-amber-200 hover:border-amber-300 hover:shadow-md' 
              : 'border-amber-300 shadow-md'
          )}
          onClick={startCollection}
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
            <FolderOpen className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">Dossier prêt immobilier</p>
            <p className="text-[10px] text-muted-foreground">6 documents requis</p>
          </div>
          <Button 
            size="sm" 
            className="shrink-0 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg"
          >
            <Search className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Collection area */}
      <div className="flex-1 overflow-y-auto">
        {step === 'search' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center h-full text-center"
          >
            <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-3">
              <FolderOpen className="w-8 h-8 text-amber-500" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">Créez un dossier complet</p>
            <p className="text-xs text-muted-foreground">Cliquez pour lancer la recherche automatique</p>
          </motion.div>
        )}

        {(step === 'collecting' || step === 'ready' || step === 'shared') && (
          <div className="grid grid-cols-2 gap-2">
            {documents.map((doc, i) => {
              const isCollected = collectedDocs.includes(i);
              const Icon = doc.icon;
              
              return (
                <motion.div
                  key={doc.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ 
                    opacity: isCollected ? 1 : 0.4, 
                    scale: isCollected ? 1 : 0.95 
                  }}
                  transition={{ delay: i * 0.03, type: "spring", stiffness: 400 }}
                  className={cn(
                    "flex items-center gap-2 bg-white rounded-xl p-2.5 border-2 transition-all",
                    isCollected ? 'border-amber-200 shadow-sm' : 'border-slate-200'
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center transition-all",
                    isCollected 
                      ? `bg-gradient-to-br ${doc.gradient} shadow` 
                      : 'bg-slate-100'
                  )}>
                    <Icon className={cn(
                      "w-4 h-4",
                      isCollected ? 'text-white' : 'text-slate-400'
                    )} />
                  </div>
                  <p className="text-[11px] font-medium truncate flex-1">{doc.name}</p>
                  {isCollected ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500 }}
                    >
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    </motion.div>
                  ) : (
                    <motion.div 
                      className="w-4 h-4 border-2 border-amber-300 border-t-amber-500 rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {step === 'ready' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                <FolderOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-sm text-amber-900">Dossier Prêt 2024</p>
                <p className="text-[10px] text-amber-700">{documents.length} documents prêts à partager</p>
              </div>
            </div>
            <Button 
              size="sm" 
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg"
              onClick={shareFolder}
            >
              <Share2 className="w-4 h-4 mr-1.5" />
              Partager
            </Button>
          </div>
        </motion.div>
      )}

      {step === 'shared' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-sm text-emerald-800">Lien de partage créé !</p>
              <p className="text-[10px] text-emerald-600">Envoyez ce lien à votre banquier</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-xl p-2.5 border border-emerald-200 shadow-sm">
            <LinkIcon className="w-4 h-4 text-emerald-500 shrink-0" />
            <span className="text-xs text-slate-600 truncate flex-1 font-mono">
              easyfinder.app/d/pret-2024
            </span>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50">
              Copier
            </Button>
          </div>
          <button onClick={reset} className="text-xs text-primary hover:underline mt-3 block mx-auto">
            Relancer la démo
          </button>
        </motion.div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ALERTS MOCKUP - Professional expiration alerts
// ═══════════════════════════════════════════════════════════════

const AlertTriangleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

function AlertsMockup() {
  const [dismissed, setDismissed] = useState<number[]>([]);
  const [snoozed, setSnoozed] = useState<number[]>([]);

  const alerts = [
    { 
      name: "Carte d'identité", 
      expires: "7 jours",
      daysLeft: 7,
      status: "urgent", 
      icon: IdCardIcon,
      gradient: "from-red-500 to-rose-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      textColor: "text-red-700"
    },
    { 
      name: "Assurance auto AXA", 
      expires: "15 jours",
      daysLeft: 15,
      status: "warning", 
      icon: InsuranceIcon,
      gradient: "from-amber-500 to-orange-500",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      textColor: "text-amber-700"
    },
    { 
      name: "Contrat de location", 
      expires: "30 jours",
      daysLeft: 30,
      status: "info", 
      icon: ContractIcon,
      gradient: "from-blue-500 to-indigo-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      textColor: "text-blue-700"
    },
  ];

  const handleSnooze = (index: number) => {
    setSnoozed(s => [...s, index]);
    setTimeout(() => {
      setSnoozed(s => s.filter(i => i !== index));
    }, 3000);
  };

  const allHandled = dismissed.length === alerts.length;

  return (
    <div className="h-full p-4 md:p-6 flex flex-col bg-gradient-to-br from-rose-50/50 to-white">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow-lg">
            <Bell className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-foreground">Alertes d'expiration</h4>
            <p className="text-[10px] text-muted-foreground">
              {alerts.length - dismissed.length} document(s) à renouveler
            </p>
          </div>
        </div>
        {!allHandled && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-100">
            <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
            <span className="text-[10px] font-medium text-rose-700">Actif</span>
          </div>
        )}
      </div>

      {/* Alerts List */}
      <div className="flex-1 overflow-y-auto space-y-3">
        <AnimatePresence>
          {alerts.map((alert, i) => {
            if (dismissed.includes(i)) return null;
            const Icon = alert.icon;
            const isSnoozed = snoozed.includes(i);
            
            return (
              <motion.div
                key={alert.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: isSnoozed ? 0.5 : 1, x: 0 }}
                exit={{ opacity: 0, x: 50, height: 0, marginBottom: 0 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                className={cn(
                  "relative overflow-hidden rounded-2xl border-2 p-4",
                  alert.bgColor,
                  alert.borderColor
                )}
              >
                {/* Progress bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-white/50">
                  <motion.div
                    initial={{ width: "100%" }}
                    className={`h-full bg-gradient-to-r ${alert.gradient}`}
                    style={{ width: `${Math.max(10, 100 - (alert.daysLeft * 3))}%` }}
                  />
                </div>

                <div className="flex items-start gap-3 pt-1">
                  <div className={cn(
                    "w-11 h-11 rounded-xl flex items-center justify-center shadow-lg",
                    `bg-gradient-to-br ${alert.gradient}`
                  )}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className={cn("font-semibold text-sm", alert.textColor)}>
                      {alert.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex items-center gap-1">
                        <Clock className={cn("w-3 h-3", alert.textColor)} />
                        <span className={cn("text-[11px] font-medium", alert.textColor)}>
                          Expire dans {alert.expires}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleSnooze(i)}
                      className={cn(
                        "p-2 rounded-xl transition-colors",
                        `hover:bg-white/60 ${alert.textColor}`
                      )}
                      title="Reporter"
                    >
                      <Clock className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDismissed(d => [...d, i])}
                      className={cn(
                        "p-2 rounded-xl transition-colors",
                        `hover:bg-white/60 ${alert.textColor}`
                      )}
                      title="Traité"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {isSnoozed && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-[10px] text-center text-muted-foreground"
                  >
                    Rappel reporté de 7 jours
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* All clear state */}
      {allHandled && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex-1 flex flex-col items-center justify-center text-center"
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-4 shadow-xl">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <p className="text-base font-semibold text-emerald-700 mb-1">Tout est à jour !</p>
          <p className="text-xs text-muted-foreground mb-4">Aucun document à renouveler</p>
          <button
            onClick={() => setDismissed([])}
            className="text-xs text-primary hover:underline font-medium"
          >
            Réinitialiser la démo
          </button>
        </motion.div>
      )}
    </div>
  );
}
